import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { UserService } from 'src/app/service/user.service';
import { User } from 'src/app/model/user';
import { FormGroup, FormBuilder } from '@angular/forms';
import { NotificationService } from 'src/app/service/notification.service';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { InfoService } from 'src/app/service/info.service';
import { forkJoin } from 'rxjs';

/** Linha no formato que o `app-people-table` espera (lê `row.user.*`). */
interface AdminRow { user: User; }

@Component({
  selector: 'app-administrators',
  templateUrl: './administrators.component.html',
  styleUrls: ['./administrators.component.scss']
})
export class AdministratorsComponent implements OnInit {
  administrators: Array<User>;
  form: FormGroup;
  loggedUser: User;

  /** Texto do filtro (busca em chip no header). */
  search = '';
  /** Estado de sort vindo do app-people-table (ordenar é do consumidor). */
  sortState: Sort = { active: 'name', direction: 'asc' };

  /** Todos os admins embrulhados em `{ user }` (formato do people-table). */
  private rows: AdminRow[] = [];
  /** Linhas visíveis (filtradas + ordenadas) — ligadas ao `[dataSource]`. */
  visibleRows: AdminRow[] = [];

  constructor(
    private userService: UserService,
    private notificationService: NotificationService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    public infoService: InfoService
  ) { }

  ngOnInit(): void {
    setTimeout(() => {
      this.getSuperUsers();
      this.userService.getUserFromToken().subscribe(user => {
        this.loggedUser = user;
      });
    });
  }

  public getSuperUsers(): void {
    this.userService.getSuperUsers().subscribe((superUsers: Array<User>) => {
      this.administrators = superUsers;
      this.rows = superUsers.map(user => ({ user }));
      this.recompute();
      this.initForm();
    });
  }

  public initForm(): void {
    this.form = this.fb.group({
      users: this.administrators
    });

    this.form.get('users').valueChanges.subscribe((users: Array<User>) => {
      const selectedUser = users[0];
      this.setSuperUserStatus(selectedUser, true);
      return users.splice(0, 1);
    });
  }

  // ─── Filtro + ordenação (consumidor do people-table) ──────────────────
  onSortChange(sort: Sort): void {
    this.sortState = sort;
    this.recompute();
  }

  recompute(): void {
    const normalize = (s: any) =>
      (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const term = normalize(this.search);

    let list = term
      ? this.rows.filter(r => normalize(
          `${r.user.name} ${r.user.email} ${r.user.profile?.affiliation?.name || ''} ${r.user.profile?.affiliation?.acronym || ''}`
        ).includes(term))
      : [...this.rows];

    const dir = this.sortState.direction === 'desc' ? -1 : 1;
    const key = (r: AdminRow): string => {
      switch (this.sortState.active) {
        case 'affiliation': return r.user.profile?.affiliation?.acronym || r.user.profile?.affiliation?.name || '';
        case 'country': return r.user.profile?.country || '';
        case 'email': return r.user.email || '';
        default: return r.user.name || '';
      }
    };
    list.sort((a, b) => key(a).localeCompare(key(b)) * dir);
    this.visibleRows = list;
  }

  public updateNotifyUser(user: User): void {
    if (user) {
      this.userService.update(user).subscribe(() => {});
    }
  }

  public setSuperUserStatus(user: User, status: boolean): void {
    if (user) {
      this.userService.updateSuperUser(user.id, status).subscribe(
        () => {
          this.notificationService.notify(
            `admin.administrators.${status ? 'added' : 'removed' }-super-user`,
            { params: { user: user.name } }
          );
          this.getSuperUsers();
        },
        () => {
          this.notificationService.notifyError(
            'admin.administrators.error-assigning-user',
            { params: { user: user.name } }
          );
        }
      );
    }
  }

  /** Remove em lote os administradores selecionados (barra flutuante). Exclui
   *  o próprio usuário logado (não pode se auto-remover). Confirma antes. */
  removeSelectedSuperUsers(rows: AdminRow[]): void {
    const users = (rows || [])
      .map(r => r.user)
      .filter(u => u && u.id !== this.loggedUser?.id);
    if (!users.length) { return; }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.administrators.confirm-remove-selected-title',
        content: 'admin.administrators.confirm-remove-selected-content',
        params: { content: { count: users.length } }
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      forkJoin(users.map(u => this.userService.updateSuperUser(u.id, false))).subscribe(
        () => {
          this.notificationService.notify(
            'admin.administrators.removed-selected',
            { params: { count: users.length } }
          );
          this.getSuperUsers();
        },
        () => {
          this.notificationService.notifyError('admin.administrators.error-assigning-user', { params: { user: '' } });
        }
      );
    });
  }

  removeSuperUser(id: number): void {
    this.userService.getUser(id).subscribe(user => {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'admin.administrators.confirm-dialog-title',
          content: 'admin.administrators.confirm-dialog-content',
          params: {
            content: { user }
          }
        }
      });

      dialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.setSuperUserStatus(user, false);
        }
      });
    });
  }
}
